import React, { useEffect, useState } from 'react';
import { ReactComponent as AddIcon } from 'assets/icons/big-add.svg';
import AddPartnerFooter from './add-partner-footer';
import { BaseModal, ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import PartnerSecondStep, {
  bankNames
} from 'components/logistics/partners/steps/partner-second-step';
import PartnerFourthStep, {
  RiderFieldType
} from 'components/logistics/partners/steps/partner-fourth-step';
import PartnerThirdStep, {
  ManagerFieldType
} from 'components/logistics/partners/steps/partner-third-step';
import { apiService } from 'utils/api';
import { PartnerFormType } from 'components/logistics/partners/steps/types';
import NumberedSteps from 'components/base/numbered-steps';
import PartnerFirstStep from 'components/logistics/partners/steps/partner-first-step';
import { toast } from 'react-toastify';

const AddPartnerSideModal = ({
  showAddPartner,
  onClose
}: {
  showAddPartner: boolean;
  onClose: () => void;
}) => {
  const emptyForm = {
    partner_name: '',
    partner_email: '',
    mobile_no: '',
    address: '',
    account_number: '',
    account_name: '',
    bank_code: '',
    location_served: '',
    riders: [
      {
        first_name: '',
        last_name: '',
        mobile_no: '',
        user_gender: '',
        file: null
      }
    ],
    logistics_admins: [
      {
        first_name: '',
        mobile_no: '',
        email: '',
        user_gender: ''
      }
    ]
  };

  const [formData, setFormData] = useState(emptyForm as unknown as PartnerFormType);
  const [location, setLocation] = useState<string[]>([]);
  const [bankName, setBankName] = useState<string[]>([]);
  const [step, setStep] = useState(1);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    const bank = bankNames.find((bank) => bank.value === bankName[0]);
    if (bank) {
      setFormData({ ...formData, bank_code: bank.code });
    }
  }, [bankName]);
  const onSubmit = (e: any) => {
    e.preventDefault();
  };

  const handleAddRiders = (e: any, i: number, field: RiderFieldType) => {
    const riders = [...formData.riders];
    if (field === 'user_gender') {
      riders[i].user_gender = e;
    } else if (field === 'profile_image') {
      riders[i].profile_image = e;
    } else {
      riders[i][field] = e.target.value;
    }
    setFormData({ ...formData });
  };

  const handleAddManagers = (e: any, i: number, field: ManagerFieldType) => {
    const logistics_admins = [...formData.logistics_admins];
    if (field === 'user_gender') {
      logistics_admins[i][field] = e;
    } else {
      logistics_admins[i][field] = e.target.value;
    }
    setFormData({ ...formData });
  };

  const handleSubmit = async () => {
    const logistics_company = {
      contact_phone_number: formatPhoneNumber(formData.mobile_no),
      name: formData.partner_name,
      address: formData.address,
      payout_account_name: formData.account_name,
      payout_account_number: formData.account_number,
      payout_bank_name: bankNames.find((bank) => bank.code === formData.bank_code)?.text,
      locations_served: location,
      payout_bank_code: formData.bank_code
    };
    const riders = formData.riders.map((rider) => {
      const data: any = {
        ...rider,
        phone_number: formatPhoneNumber(rider.phone_number),
        email: rider.email
      };
      // eslint-disable-next-line
      const { mobile_no, file, ...otherData } = data;
      return otherData;
    });
    const logistics_admins = formData.logistics_admins.map((manager) => {
      const data: any = { ...manager, phone_number: formatPhoneNumber(manager.phone_number) };
      delete data.mobile_no;
      return data;
    });
    try {
      await apiService.post('logistics/register-all-logistics', {
        data: {
          logistics_company,
          riders,
          logistics_admins
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage message='Partner added successfully' type='success' />);
    } catch {
      toast(<ToastMessage message='Something went wrong' type='error' />);
    } finally {
      onClose();
    }
  };
  const firstStepNotValid = () => {
    return (
      !formData.partner_name ||
      !formData.address ||
      !formData.mobile_no ||
      !formData.partner_email ||
      location.length === 0
    );
  };
  const secondStepNotValid = () => {
    return !formData?.account_number || !formData?.account_number || bankName.length === 0;
  };

  const stepIsDisabled = () => {
    if (step === 1) {
      return firstStepNotValid();
    } else if (step === 2) {
      return secondStepNotValid();
    } else {
      return;
    }
  };
  const onColoredTextClick = () => {
    if (step === 3) {
      const logistics_admins = [
        ...formData.logistics_admins,
        {
          first_name: '',
          phone_number: '',
          email: '',
          user_gender: '',
          last_name: ''
        }
      ];
      setFormData({
        ...formData,
        logistics_admins: [...logistics_admins]
      });
    } else if (step === 4) {
      const riders = [
        ...formData.riders,
        {
          first_name: '',
          last_name: '',
          phone_number: '',
          email: '',
          user_gender: '',
          profile_image: ''
        }
      ];
      setFormData({
        ...formData,
        riders: [...riders]
      });
    } else {
      return;
    }
  };
  return (
    <BaseModal
      position='right'
      isOpen={showAddPartner}
      onRequestClose={onClose}
      title={step === 4 ? 'Add Rider(s)' : step === 3 ? 'Add Manager(s)' : ''}
      titleIconColor='gray'
      titleIcon={<AddIcon />}
      titleCta={
        <span role='button' className='text-red-5' onClick={onColoredTextClick}>
          {step === 4 ? 'Add riders' : step === 3 ? 'Additional manager' : ''}
        </span>
      }
      hasBackButton={step >= 2}
      onBackButton={() => setStep(step - 1)}
      footer={
        <AddPartnerFooter
          step={step}
          onClickNext={() => {
            if (step !== 4) {
              setStep(step + 1);
            } else {
              handleSubmit();
            }
          }}
          onClickSkip={() => {
            if (step !== 4) setStep(step + 1);
          }}
          disabled={stepIsDisabled()}
        />
      }>
      <NumberedSteps step={step} />

      <form onSubmit={onSubmit} className='mt-6'>
        {step === 1 && (
          <PartnerFirstStep
            handleChange={handleChange}
            location={location}
            setLocation={setLocation}
            formData={formData}
          />
        )}
        {step === 2 && (
          <PartnerSecondStep
            handleChange={handleChange}
            bankName={bankName}
            setBankName={setBankName}
            formData={formData}
          />
        )}
        {step === 3 && (
          <PartnerThirdStep handleAddManagers={handleAddManagers} formData={formData} />
        )}
        {step === 4 && <PartnerFourthStep handleAddRiders={handleAddRiders} formData={formData} />}
      </form>
    </BaseModal>
  );
};

export default AddPartnerSideModal;
